import React, { useEffect, useMemo, useState } from "react";
import "./DashboardPage.css";
import { getOrderReportByDayEnterprise, getOrderReportSummary } from "../api/orderReportsApi";
import { getPaymentManagementSummary } from "../api/paymentReportsApi";
import { getSupplierDepositReport } from "../api/supplierDepositsApi";
import { getDropshipSenderStock, searchDropshipSenderStockProducts, getDropshipSenderStockAnalogs } from "../api/fulfillmentApi";
import { getBusinessStoreRatingSummary } from "../api/businessStoreRatingsApi";
import { API_BASE_URL } from "../config";
import { formatDateInKyiv, formatKyivTime, kyivDateTimeAtCurrentTime, shiftDateByDays } from "../kyivTime";

const pageStyle = {
    padding: "24px",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: "18px",
    maxWidth: "1440px",
    margin: "0 auto",
    boxSizing: "border-box",
};

const kpiToneClass = {
    default: "",
    blue: "tone-blue",
    good: "tone-good",
    bad: "tone-bad",
    warn: "tone-warn",
};

const balanceSourceLabels = {
    manual_current_period: "Введено за период",
    carried_forward: "Перенесено",
    balance_checkpoint: "От факта",
};

const formatAmount = (value) => {
    if (value === null || value === undefined || value === "") {
        return "—";
    }
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return String(value);
    }
    return number.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDays = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return "—";
    }
    return number.toLocaleString("ru-RU", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const formatCount = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return "—";
    }
    return number.toLocaleString("ru-RU", { maximumFractionDigits: 0 });
};

const formatDateTime = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("ru-RU", { timeZone: "Europe/Kiev" });
};

const percentOf = (numerator, denominator) => {
    const numeratorValue = Number(numerator || 0);
    const denominatorValue = Number(denominator || 0);
    if (!Number.isFinite(numeratorValue) || !Number.isFinite(denominatorValue) || denominatorValue === 0) {
        return "0,00%";
    }
    return `${formatAmount((numeratorValue * 100) / denominatorValue)}%`;
};

const sumAmounts = (rows, fieldName) => rows.reduce((total, row) => {
    const value = Number(row?.[fieldName] || 0);
    return Number.isFinite(value) ? total + value : total;
}, 0);

const formatDashboardError = (error) => {
    const detail = error?.response?.data?.detail;
    if (detail) {
        return detail;
    }
    if (error?.message === "Network Error") {
        const origin = typeof window !== "undefined" ? window.location.origin : "unknown origin";
        return `Network Error: API ${API_BASE_URL} недоступен из ${origin}. Проверь, что FastAPI запущен на 127.0.0.1:8000 и CORS перезагружен.`;
    }
    return error?.message || "Не удалось загрузить главную страницу.";
};

const MetricCard = ({ label, value, valueStyle, secondLabel, secondValue, tone = "default" }) => (
    <div className={`db-kpi${kpiToneClass[tone] ? ` ${kpiToneClass[tone]}` : ""}`}>
        <span className="lbl">{label}</span>
        <span className="val" style={valueStyle}>{value}</span>
        {secondLabel ? (
            <div className="sub">
                <span className="slbl">{secondLabel}</span>
                <span className="sval">{secondValue}</span>
            </div>
        ) : null}
    </div>
);

const DashboardPage = () => {
    const [dashboardDate, setDashboardDate] = useState(() => formatDateInKyiv(new Date()));
    const [orderSummary, setOrderSummary] = useState(null);
    const [paymentManagement, setPaymentManagement] = useState(null);
    const [depositReport, setDepositReport] = useState(null);
    const [yesterdayDepositReport, setYesterdayDepositReport] = useState(null);
    const [weekDepositReport, setWeekDepositReport] = useState(null);
    const [profitByDay, setProfitByDay] = useState([]);
    const [comparisonProfitByDay, setComparisonProfitByDay] = useState([]);
    const [comparisonCutoff, setComparisonCutoff] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const loadDashboard = async () => {
        setLoading(true);
        setError("");
        try {
            const now = new Date();
            const todayDate = formatDateInKyiv(now);
            const yesterdayDate = shiftDateByDays(todayDate, -1);
            const weekStartDate = shiftDateByDays(todayDate, -6);
            const comparisonWeekStartDate = shiftDateByDays(weekStartDate, -28);
            const comparisonWeekEndDate = shiftDateByDays(todayDate, -28);
            const comparisonEndAt = kyivDateTimeAtCurrentTime(comparisonWeekEndDate, now);
            const monthStartDate = `${todayDate.slice(0, 7)}-01`;
            setDashboardDate(todayDate);
            setComparisonCutoff(formatKyivTime(comparisonEndAt));
            const [orders, payments, deposits, previousDayDeposits, weekDeposits, profitDays, comparisonProfitDays] = await Promise.all([
                getOrderReportSummary({ periodFrom: todayDate, periodTo: todayDate }),
                getPaymentManagementSummary({ periodFrom: monthStartDate, periodTo: todayDate }),
                getSupplierDepositReport({ periodFrom: todayDate, periodTo: todayDate }),
                getSupplierDepositReport({ periodFrom: yesterdayDate, periodTo: yesterdayDate }),
                getSupplierDepositReport({ periodFrom: weekStartDate, periodTo: todayDate }),
                getOrderReportByDayEnterprise({ periodFrom: weekStartDate, periodTo: todayDate }),
                getOrderReportByDayEnterprise({ periodFrom: comparisonWeekStartDate, periodTo: comparisonEndAt }),
            ]);
            setOrderSummary(orders);
            setPaymentManagement(payments);
            setDepositReport(deposits);
            setYesterdayDepositReport(previousDayDeposits);
            setWeekDepositReport(weekDeposits);
            setProfitByDay(profitDays || []);
            setComparisonProfitByDay(comparisonProfitDays || []);
        } catch (loadError) {
            setError(formatDashboardError(loadError));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDashboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const accountRows = useMemo(() => paymentManagement?.account_movements || [], [paymentManagement]);
    const accountTotal = useMemo(() => sumAmounts(accountRows, "calculated_closing_balance"), [accountRows]);
    const depositRows = useMemo(() => {
        const yesterdayById = Object.fromEntries((yesterdayDepositReport?.groups || []).map((group) => [group.id, group]));
        const weekById = Object.fromEntries((weekDepositReport?.groups || []).map((group) => [group.id, group]));
        return (depositReport?.groups || [])
            // Агентские депозиты (settlement_mode='agency') не показываем на дневном борде:
            // они самопополняются вне системы, поэтому прогноз/остаток по списаниям неинформативны.
            .filter((group) => group.settlement_mode !== "agency")
            .map((group) => {
            const yesterdayGroup = yesterdayById[group.id] || {};
            const weekGroup = weekById[group.id] || {};
            const weekOrderAmount = Number(weekGroup.writeoffs?.amount || 0);
            const averageDailyOrderAmount = weekOrderAmount / 7;
            const balance = Number(group.calculated_balance || 0);
            const forecastDays = averageDailyOrderAmount > 0 ? balance / averageDailyOrderAmount : null;
            return {
                ...group,
                today_orders: group.writeoffs || { count: 0, amount: "0.00" },
                yesterday_orders: yesterdayGroup.writeoffs || { count: 0, amount: "0.00" },
                average_daily_order_amount: averageDailyOrderAmount,
                forecast_days: forecastDays,
            };
        });
    }, [depositReport, yesterdayDepositReport, weekDepositReport]);
    const depositTotal = useMemo(() => sumAmounts(depositRows, "calculated_balance"), [depositRows]);

    return (
        <div className="dashboard-page" style={pageStyle}>
            <section className="db-card db-toolbar">
                <div className="db-toolbar-head">
                    <div className="db-head">
                        <h1>Главная</h1>
                        <p>Дневной борд за {dashboardDate}. Данные читаются из уже рассчитанных отчетов.</p>
                    </div>
                    <div className="db-toolbar-actions">
                        <button type="button" className="db-btn db-btn-primary" onClick={loadDashboard} disabled={loading}>
                            {loading ? "Загрузка..." : "Обновить"}
                        </button>
                    </div>
                </div>
                {error ? <div className="db-note err"><span className="dot" />{error}</div> : null}
            </section>

            <section className="db-kpi-grid">
                <MetricCard label="Сумма заказов" value={formatAmount(orderSummary?.order_financial_amount)} secondLabel="Выручка продаж" secondValue={formatAmount(orderSummary?.sale_amount)} tone="blue" />
                <MetricCard
                    label="ВП заказов / %ВП"
                    value={`${formatAmount(orderSummary?.order_gross_profit_amount)}/${percentOf(orderSummary?.order_gross_profit_amount, orderSummary?.order_financial_amount)}`}
                    valueStyle={{ fontSize: 20 }}
                    secondLabel="ЧП заказов"
                    secondValue={formatAmount(orderSummary?.order_net_profit_amount)}
                    tone="good"
                />
                <MetricCard label="ВП продаж" value={formatAmount(orderSummary?.gross_profit_amount)} secondLabel="ЧП продаж" secondValue={formatAmount(orderSummary?.net_profit_amount)} tone="good" />
                <MetricCard label="Количество заказов" value={orderSummary?.order_financial_count || 0} secondLabel="Количество продаж" secondValue={orderSummary?.sales_count || 0} />
                <MetricCard label="Отказы" value={orderSummary?.cancelled_count || 0} secondLabel="% отказов" secondValue={`${formatAmount(orderSummary?.refusal_rate)}%`} tone="bad" />
            </section>

            <OrderNetProfitChart rows={profitByDay} comparisonRows={comparisonProfitByDay} comparisonCutoff={comparisonCutoff} />

            <SenderStockPanel />

            <RatingPillsPanel />

            <section className="db-card">
                <div className="db-panel-head">
                    <div>
                        <h2>Остатки средств</h2>
                        <p>Расчетные остатки по счетам на текущий момент.</p>
                    </div>
                    <div className="db-panel-total">
                        <span className="plbl">Итого на счетах</span>
                        <div className="pval">{formatAmount(accountTotal)}</div>
                    </div>
                </div>
                <div className="db-panel-pad">
                    <AccountBalancesTable rows={accountRows} />
                </div>
            </section>

            <section className="db-card">
                <div className="db-panel-head">
                    <div>
                        <h2>Депозиты поставщиков</h2>
                        <p>Остаток, заказы сегодня/вчера и прогноз дней по среднему списанию за последние 7 дней.</p>
                    </div>
                    <div className="db-panel-total">
                        <span className="plbl">Итого на депозитах</span>
                        <div className="pval">{formatAmount(depositTotal)}</div>
                    </div>
                </div>
                <div className="db-panel-pad">
                    <DepositsTable rows={depositRows} />
                </div>
            </section>

        </div>
    );
};

const profitChartColors = ["#2563eb", "#16a34a", "#f59e0b", "#8b5cf6", "#db2777", "#0891b2", "#65a30d", "#ea580c"];

const formatDayLabel = (value) => {
    const [year, month, day] = String(value || "").split("-");
    return year && month && day ? `${day}.${month}` : value;
};

const formatProfitAmount = (value) => `${formatAmount(value)} ₴`;

const OrderNetProfitChart = ({ rows, comparisonRows, comparisonCutoff }) => {
    const [selected, setSelected] = useState(null);
    const enterprises = useMemo(() => {
        const byCode = new Map();
        rows.forEach((day) => (day.enterprises || []).forEach((enterprise) => {
            const current = byCode.get(enterprise.enterprise_code) || { ...enterprise, net_profit_amount: 0 };
            current.net_profit_amount += Number(enterprise.net_profit_amount || 0);
            byCode.set(enterprise.enterprise_code, current);
        }));
        return [...byCode.values()]
            .sort((left, right) => left.enterprise_name.localeCompare(right.enterprise_name, "ru"))
            .map((enterprise, index) => ({ ...enterprise, color: profitChartColors[index % profitChartColors.length] }));
    }, [rows]);
    const enterpriseByCode = useMemo(() => new Map(enterprises.map((enterprise) => [enterprise.enterprise_code, enterprise])), [enterprises]);
    const totalProfit = rows.reduce((total, day) => total + Number(day.total_net_profit_amount || 0), 0);
    const comparisonTotal = comparisonRows.reduce((total, day) => total + Number(day.total_net_profit_amount || 0), 0);
    const values = [0, ...rows.map((day) => Number(day.total_net_profit_amount || 0)), ...comparisonRows.map((day) => Number(day.total_net_profit_amount || 0))];
    const maxValue = Math.max(...values, 0);
    const minValue = Math.min(...values, 0);
    const range = Math.max(maxValue - minValue, 1);
    const zeroPosition = (maxValue / range) * 100;
    const zeroBottom = ((0 - minValue) / range) * 100;
    const chartTrackHeight = 220;
    const chartLabelHeight = 30;
    const chartTotalHeight = chartLabelHeight + chartTrackHeight + 20;
    const pointForComparison = (value, index) => ({
        x: ((index + 0.5) / Math.max(rows.length, 1)) * 100,
        y: ((maxValue - Number(value || 0)) / range) * 100,
    });
    const comparisonPoints = rows.map((_, index) => pointForComparison(comparisonRows[index]?.total_net_profit_amount, index));
    const comparisonPolyline = comparisonPoints.map((point) => `${point.x},${point.y}`).join(" ");

    if (!rows.length) return null;

    return (
        <section className="db-card db-profit-chart">
            <div className="db-panel-head">
                <div>
                    <h2>ЧП заказов по дням</h2>
                    <p>Чистая прибыль оформленных заказов по предприятиям.</p>
                </div>
                <div className="db-panel-total">
                    <span className="plbl">Всего за период</span>
                    <div className="pval">{formatProfitAmount(totalProfit)}</div>
                </div>
            </div>
            <div className="db-profit-scroll">
                <div className="db-profit-bars" style={{ "--db-profit-columns": rows.length, "--db-profit-zero-y": `${chartLabelHeight + (zeroPosition / 100) * chartTrackHeight}px` }} role="list" aria-label="ЧП заказов по дням">
                    <div className="db-profit-zero" aria-hidden="true" />
                    <svg className="db-profit-comparison-line" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                        <polyline points={comparisonPolyline} />
                    </svg>
                    {rows.map((day, index) => {
                        let positiveOffset = 0;
                        let negativeOffset = 0;
                        const total = Number(day.total_net_profit_amount || 0);
                        return (
                            <div className="db-profit-column" key={day.date} role="listitem">
                                <span className="db-profit-total">{formatProfitAmount(total)}</span>
                                <div className="db-profit-track">
                                    {(day.enterprises || []).map((segment) => {
                                        const amount = Number(segment.net_profit_amount || 0);
                                        if (!amount) return null;
                                        const enterprise = enterpriseByCode.get(segment.enterprise_code);
                                        const height = (Math.abs(amount) / range) * 100;
                                        const bottom = amount > 0 ? zeroBottom + positiveOffset : undefined;
                                        const top = amount < 0 ? zeroPosition + negativeOffset : undefined;
                                        if (amount > 0) positiveOffset += height;
                                        else negativeOffset += height;
                                        const isSelected = selected?.type === "enterprise" && selected.date === day.date && selected.enterpriseCode === segment.enterprise_code;
                                        return <button key={segment.enterprise_code} type="button" className={`db-profit-segment${isSelected ? " selected" : ""}`} style={{ height: `${height}%`, bottom: bottom === undefined ? "auto" : `${bottom}%`, top: top === undefined ? "auto" : `${top}%`, backgroundColor: enterprise?.color }} aria-label={`${enterprise?.enterprise_name}: ${formatProfitAmount(amount)}`} onClick={() => setSelected(isSelected ? null : { type: "enterprise", date: day.date, enterpriseCode: segment.enterprise_code, enterpriseName: enterprise?.enterprise_name, amount, total })} />;
                                    })}
                                </div>
                                <span className="db-profit-day">{formatDayLabel(day.date)}</span>
                            </div>
                        );
                    })}
                    {rows.map((day, index) => {
                        const point = comparisonPoints[index];
                        const comparison = Number(comparisonRows[index]?.total_net_profit_amount || 0);
                        const isSelected = selected?.type === "comparison" && selected.date === day.date;
                        const cutoffLabel = index === rows.length - 1 && comparisonCutoff ? ` до ${comparisonCutoff}` : "";
                        return <button key={`comparison-${day.date}`} type="button" className={`db-profit-comparison-point${isSelected ? " selected" : ""}`} style={{ left: `${point.x}%`, top: `${((chartLabelHeight + (point.y / 100) * chartTrackHeight) / chartTotalHeight) * 100}%` }} aria-label={`${formatDayLabel(day.date)}, 4 недели назад${cutoffLabel}: ${formatProfitAmount(comparison)}`} onClick={() => setSelected(isSelected ? null : { type: "comparison", date: day.date, amount: comparison, cutoffLabel })} />;
                    })}
                </div>
            </div>
            {selected ? <div className="db-profit-tooltip" role="status"><b>{formatDayLabel(selected.date)}</b><span>{selected.type === "comparison" ? `ЧП 4 недели назад${selected.cutoffLabel || ""}` : selected.enterpriseName}</span><strong>{formatProfitAmount(selected.amount)}{selected.type === "enterprise" ? ` · ${percentOf(selected.amount, selected.total)}` : ""}</strong></div> : null}
            <div className="db-profit-legend" aria-label="Легенда предприятий">
                {enterprises.map((enterprise) => <div className="db-profit-legend-item" key={enterprise.enterprise_code}><span className="db-profit-legend-color" style={{ backgroundColor: enterprise.color }} /><span>{enterprise.enterprise_name}</span><b>{formatProfitAmount(enterprise.net_profit_amount)} · {percentOf(enterprise.net_profit_amount, totalProfit)}</b></div>)}
                <div className="db-profit-legend-item db-profit-comparison-legend"><span className="db-profit-line-swatch" /><span>ЧП 4 недели назад</span><b>{formatProfitAmount(comparisonTotal)}</b></div>
            </div>
        </section>
    );
};

const AccountBalancesTable = ({ rows }) => (
    <div className="db-tbl-wrap">
        <table className="db-tbl">
            <thead>
                <tr>
                    <th>Юрлицо</th>
                    <th>Счет</th>
                    <th className="num">Входящие</th>
                    <th className="num">Исходящие</th>
                    <th className="num">Расчетный остаток</th>
                    <th>Источник</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => (
                    <tr key={row.account_id}>
                        <td>{row.business_entity_name || "—"}</td>
                        <td>
                            <div className="db-nm">{row.account_label || "—"}</div>
                            <div className="db-sub-meta">{row.account_number || "—"}</div>
                        </td>
                        <td className="num">{formatAmount(row.incoming?.amount)}</td>
                        <td className="num">{formatAmount(row.outcoming?.amount)}</td>
                        <td className="num" style={{ fontWeight: 800 }}>{formatAmount(row.calculated_closing_balance)}</td>
                        <td>
                            {balanceSourceLabels[row.opening_balance_source] || "—"}
                            <div className="db-sub-meta">{row.balance_checkpoint_date || row.opening_balance_source_period || "нет факта"}</div>
                        </td>
                    </tr>
                ))}
                {rows.length === 0 ? <tr className="db-empty-row"><td colSpan={6}>Нет данных за период</td></tr> : null}
            </tbody>
        </table>
    </div>
);

const DepositsTable = ({ rows }) => (
    <div className="db-tbl-wrap">
        <table className="db-tbl">
            <thead>
                <tr>
                    <th>Депозит</th>
                    <th>Отправитель</th>
                    <th>Юрлицо</th>
                    <th className="num">Остаток</th>
                    <th className="num">Заказы сегодня</th>
                    <th className="num">Заказы вчера</th>
                    <th className="num">Прогноз</th>
                    <th>Факт</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((group) => {
                    const links = group.active_links || [];
                    return (
                        <tr key={group.id}>
                            <td>
                                <div className="db-nm">{group.name}</div>
                                <div className="db-sub-meta">{group.description || "—"}</div>
                            </td>
                            <td>
                                {links.length ? links.map((link) => (
                                    <div key={link.id}>{link.supplier_code} · {link.supplier_name || "—"}</div>
                                )) : "—"}
                            </td>
                            <td>
                                {links.length ? links.map((link) => (
                                    <div key={link.id}>{link.business_entity_name || "—"}</div>
                                )) : "—"}
                            </td>
                            <td className="num" style={{ fontWeight: 800 }}>{formatAmount(group.calculated_balance)}</td>
                            <td className="num">
                                {formatAmount(group.today_orders?.amount)}
                                <div className="db-sub-meta">{group.today_orders?.count || 0} заказов</div>
                            </td>
                            <td className="num">
                                {formatAmount(group.yesterday_orders?.amount)}
                                <div className="db-sub-meta">{group.yesterday_orders?.count || 0} заказов</div>
                            </td>
                            <td className="num">
                                <strong>{group.forecast_days === null ? "—" : `${formatDays(group.forecast_days)} дн.`}</strong>
                                <div className="db-sub-meta">средн. {formatAmount(group.average_daily_order_amount)}/день</div>
                            </td>
                            <td>
                                {formatAmount(group.balance_checkpoint_amount)}
                                <div className="db-sub-meta">{group.balance_checkpoint_date || "нет факта"}</div>
                            </td>
                        </tr>
                    );
                })}
                {rows.length === 0 ? <tr className="db-empty-row"><td colSpan={8}>Нет данных за период</td></tr> : null}
            </tbody>
        </table>
    </div>
);


const senderStockProductTitle = (item) => [item?.sku, item?.name_ua || item?.name_ru].filter(Boolean).join(" · ");

const stockBadgeClass = (stock) => `db-stock-badge ${Number(stock || 0) > 0 ? "pos" : "zero"}`;
const analogGroupDefinitions = [
    { key: "same_brand_line_dose", label: "Тот же бренд, линейка и дозировка" },
    { key: "same_dose", label: "Та же дозировка" },
    { key: "other", label: "Другие похожие товары" },
];

const SenderStockPanel = () => {
    const [query, setQuery] = useState("");
    const [searchRows, setSearchRows] = useState([]);
    const [data, setData] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [loading, setLoading] = useState(false);
    const [panelError, setPanelError] = useState("");
    const [analogs, setAnalogs] = useState(null);
    const [analogsLoading, setAnalogsLoading] = useState(false);
    const [analogsInStockOnly, setAnalogsInStockOnly] = useState(true);

    const loadStock = async (product) => {
        if (!product?.sku) return;
        setPanelError("");
        setSelectedProduct(product);
        setLoading(true);
        try {
            const payload = await getDropshipSenderStock(product.sku);
            setData(payload);
        } catch (err) {
            setPanelError(err?.response?.data?.detail || err.message || "Ошибка загрузки остатков по отправителям");
        } finally {
            setLoading(false);
        }
    };

    const loadAnalogs = async (inStockOnly = analogsInStockOnly) => {
        const target = data?.product || selectedProduct;
        if (!target?.sku) return;
        setPanelError("");
        setAnalogsLoading(true);
        try {
            const payload = await getDropshipSenderStockAnalogs(target.sku, inStockOnly);
            setAnalogs(payload);
        } catch (err) {
            setPanelError(err?.response?.data?.detail || err.message || "Ошибка загрузки аналогов");
        } finally {
            setAnalogsLoading(false);
        }
    };

    const runSearch = async () => {
        const trimmed = query.trim();
        setAnalogs(null);
        if (trimmed.length < 2) {
            setSearchRows([]);
            setData(null);
            return;
        }
        setPanelError("");
        setLoading(true);
        try {
            const rows = await searchDropshipSenderStockProducts(trimmed);
            const list = Array.isArray(rows) ? rows : [];
            setSearchRows(list);
            if (list.length === 1) {
                await loadStock(list[0]);
            }
        } catch (err) {
            setPanelError(err?.response?.data?.detail || err.message || "Ошибка поиска товара по отправителям");
        } finally {
            setLoading(false);
        }
    };

    const rows = (data?.rows || []).filter((item) => Number(item.stock || 0) > 0);
    const totalStock = rows.reduce((acc, item) => acc + Number(item.stock || 0), 0);
    const product = data?.product || selectedProduct;
    const productBarcode = product?.barcode || "";
    const competitorPrice = product?.competitor_price || "";

    return (
        <section className="db-card">
            <div className="db-panel-head">
                <div>
                    <h2>Остатки по отправителям</h2>
                    <p>Отправителей: {rows.length}; общий остаток: {totalStock}</p>
                </div>
                {product ? (
                    <button type="button" className="db-btn db-btn-ghost db-btn-sm" onClick={() => loadAnalogs()} disabled={analogsLoading}>
                        {analogsLoading ? "Загрузка аналогов..." : "Показать аналоги"}
                    </button>
                ) : null}
            </div>
            <div className="db-panel-pad">
                <div className="db-filters">
                    <div className="db-fld">
                        <label>Базовый код или наименование</label>
                        <input
                            className="db-inp"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    runSearch();
                                }
                            }}
                            placeholder="SKU, barcode или часть названия"
                        />
                    </div>
                    <button type="button" className="db-btn db-btn-primary" onClick={runSearch} disabled={loading}>
                        {loading ? "Поиск..." : "Найти"}
                    </button>
                </div>

                {panelError ? <p className="db-error-text">{panelError}</p> : null}

                {searchRows.length > 0 ? (
                    <div className="db-search-results">
                        {searchRows.map((item) => {
                            const active = product?.sku === item.sku;
                            return (
                                <button
                                    type="button"
                                    key={`${item.sku}-${item.match_source}`}
                                    className={`db-search-item${active ? " active" : ""}`}
                                    onClick={() => loadStock(item)}
                                >
                                    <strong>{senderStockProductTitle(item)}</strong>
                                    <span className="db-mut" style={{ marginLeft: 8 }}>
                                        {item.barcode || ""}
                                        {item.supplier_product_id ? ` / код отправителя ${item.supplier_product_id}` : ""}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                ) : null}

                {product ? (
                    <div className="db-callout muted" style={{ marginTop: 14 }}>
                        <span className="ic">◆</span>
                        <span>
                            <b>Остатки по товару:</b> {product.name_ua || product.name_ru || product.sku} <span className="db-mut">{product.sku}</span>
                            {analogs?.base_sku && product.sku !== analogs.base_sku ? <span className="db-pill r" style={{ marginLeft: 8 }}>аналог</span> : null}
                        </span>
                    </div>
                ) : null}

                <div className="db-tbl-wrap" style={{ marginTop: 14 }}>
                    <table className="db-tbl" style={{ minWidth: 1240 }}>
                        <thead>
                            <tr>
                                <th>Отправитель</th>
                                <th>Код</th>
                                <th>SalesDrive ID</th>
                                <th>Код товара у отправителя</th>
                                <th>Штрихкод</th>
                                <th className="num">Остаток</th>
                                <th className="num">Себестоимость</th>
                                <th className="num">РРЦ</th>
                                <th className="num">Розница расчетная</th>
                                <th className="num" title="Цена выбранного отправителя из последней подготовки выгрузки основного аккаунта Prom. Не подтверждает цену на сайте.">Prom: к выгрузке</th>
                                <th className="num">Цена конкурента</th>
                                <th>Обновлено</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((item) => (
                                <tr key={item.sender_code}>
                                    <td className="db-nm">{item.sender_name}</td>
                                    <td>{item.sender_code}</td>
                                    <td>{item.salesdrive_supplier_id || ""}</td>
                                    <td>{item.supplier_product_code || ""}</td>
                                    <td>{productBarcode}</td>
                                    <td className="num"><span className={stockBadgeClass(item.stock)}>{item.stock || 0}</span></td>
                                    <td className="num">{item.cost_price || ""}</td>
                                    <td className="num">{item.rrp_price || ""}</td>
                                    <td className="num">{item.retail_price || ""}</td>
                                    <td className="num">
                                        {item.prom_price || "—"}
                                        {item.prom_price != null ? (
                                            <div style={{ fontSize: 11, opacity: 0.7 }} title="Цена из последней подготовки выгрузки; цена на сайте Prom может отличаться">
                                                {item.prom_price_updated_at ? `Расчёт: ${new Date(item.prom_price_updated_at).toLocaleString()}` : "Подготовлено к выгрузке"}
                                            </div>
                                        ) : <span style={{ fontSize: 11, opacity: 0.7 }}>Нет выбранного предложения</span>}
                                    </td>
                                    <td className="num">{competitorPrice}</td>
                                    <td>{item.updated_at ? new Date(item.updated_at).toLocaleString() : ""}</td>
                                </tr>
                            ))}
                            {!rows.length ? (
                                <tr className="db-empty-row"><td colSpan={12}>{product ? "Нет отправителей с положительным остатком" : "Выбери товар для просмотра остатков"}</td></tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>

                {analogs ? (
                    <div style={{ marginTop: 18 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                            <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>
                                Аналоги{analogs.analogs?.length ? ` (${analogs.analogs.length})` : ""}
                            </h3>
                            <label className="db-sub-meta" style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                                <input
                                    type="checkbox"
                                    checked={analogsInStockOnly}
                                    onChange={(event) => {
                                        const inStockOnly = event.target.checked;
                                        setAnalogsInStockOnly(inStockOnly);
                                        loadAnalogs(inStockOnly);
                                    }}
                                />
                                Только в наличии
                            </label>
                        </div>
                        <p className="db-sub-meta" style={{ marginBottom: 10 }}>Клик по аналогу загрузит его остатки по отправителям выше.</p>
                        {analogs.analogs?.length ? (
                            analogGroupDefinitions.map((group) => {
                                const groupAnalogs = analogs.analogs.filter((analog) => (analog.match_group || "other") === group.key);
                                if (!groupAnalogs.length) return null;
                                return <div key={group.key} style={{ marginTop: 16 }}>
                                    <h4 style={{ margin: "0 0 8px", fontSize: 14 }}>{group.label}</h4>
                                    <div className="db-tbl-wrap">
                                        <table className="db-tbl" style={{ minWidth: 1040 }}>
                                            <thead>
                                                <tr>
                                                    <th>Наименование</th>
                                                    <th>Форма</th>
                                                    <th className="num">Кол-во</th>
                                                    <th className="num">Суммарный остаток</th>
                                                    <th className="num">Себестоимость (min)</th>
                                                    <th className="num">Розница (min)</th>
                                                    <th className="num">Цена конкурента</th>
                                                    <th>Обновлено</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                        {groupAnalogs.map((analog) => (
                                            <tr
                                                key={analog.sku}
                                                className={`click${product?.sku === analog.sku ? " active" : ""}`}
                                                onClick={() => loadStock({
                                                    sku: analog.sku,
                                                    name_ua: analog.name_ua,
                                                    name_ru: analog.name_ru,
                                                    barcode: analog.barcode,
                                                })}
                                            >
                                                <td>
                                                    <span className="db-nm">{analog.name_ua || analog.name_ru || analog.sku}</span>
                                                    <span className="db-mut" style={{ marginLeft: 8 }}>{analog.sku}</span>
                                                </td>
                                                <td>{analog.form || ""}</td>
                                                <td className="num">{analog.quantity || ""}</td>
                                                <td className="num">
                                                    <span className={stockBadgeClass(analog.total_stock)}>{analog.total_stock || 0}</span>
                                                    {analog.senders_with_stock ? <span className="db-mut" style={{ marginLeft: 6, fontSize: 11 }}>{analog.senders_with_stock} отпр.</span> : null}
                                                </td>
                                                <td className="num">{analog.min_cost_price || ""}</td>
                                                <td className="num">{analog.min_retail_price || ""}</td>
                                                <td className="num">{analog.competitor_price || ""}</td>
                                                <td>{analog.updated_at ? new Date(analog.updated_at).toLocaleString() : ""}</td>
                                            </tr>
                                        ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>;
                            })
                        ) : (
                            <p className="db-mut">Аналоги не найдены</p>
                        )}
                    </div>
                ) : null}
            </div>
        </section>
    );
};

const ratingToneClass = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return "";
    if (number < 4) return "low";
    if (number < 4.5) return "mid";
    return "";
};

// Тот же компонент, что и в Организация → Рейтинг (og-risk-row/og-risk-chip):
// бар + факт/остаток лимита за period_days, плюс отдельная строка "среднем в
// день" (day_threshold = остаток лимита / period_days, может уйти в минус).
const RiskMetric = ({ label, block }) => {
    const remaining = Number(block?.two_week_threshold || 0);
    const fact = Number(block?.fact || 0);
    const perDay = Number(block?.day_threshold || 0);
    const budget = remaining + fact;
    const pct = budget > 0 ? Math.max(0, Math.min(100, (remaining / budget) * 100)) : 0;
    const bad = remaining <= 0;
    return (
        <div className="db-risk-row">
            <div className={`db-risk-chip${bad ? " bad" : ""}`}>
                <span className="lbl">{label}</span>
                <span className="bar"><i style={{ width: `${pct}%` }} /></span>
                <span className="val">{formatCount(fact)}/{formatDays(remaining)}</span>
            </div>
            <div className={`db-risk-day${bad ? " bad" : ""}`}>≈{formatDays(perDay)}/день</div>
        </div>
    );
};

const RatingPillsPanel = () => {
    const [rows, setRows] = useState([]);
    const [periodDays, setPeriodDays] = useState(14);
    const [lastLoadedAt, setLastLoadedAt] = useState(null);
    const [loading, setLoading] = useState(false);
    const [panelError, setPanelError] = useState("");

    useEffect(() => {
        let active = true;
        (async () => {
            setLoading(true);
            setPanelError("");
            try {
                const summary = await getBusinessStoreRatingSummary();
                if (!active) return;
                setRows(summary?.rows || []);
                setPeriodDays(Number(summary?.settings?.period_days || 14));
                setLastLoadedAt(summary?.latest_data_run?.finished_at || summary?.latest_data_run?.started_at || null);
            } catch (err) {
                if (!active) return;
                setPanelError(err?.response?.data?.detail || err.message || "Не удалось загрузить рейтинг таблетки.");
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, []);

    const problematic = rows.filter((row) => (
        Number(row.refusal?.two_week_threshold || 0) <= 0
        || Number(row.shipping?.two_week_threshold || 0) <= 0
        || Number(row.unprocessed?.two_week_threshold || 0) <= 0
    )).length;

    return (
        <section className="db-card">
            <div className="db-panel-head">
                <div>
                    <h2>Рейтинг таблетки</h2>
                    <p>Магазинов: {rows.length}; проблемных: {problematic}. Факт/остаток лимита за {periodDays}д, ниже — среднедневной остаток.</p>
                </div>
                <span className="db-sub-meta">Последняя загрузка рейтинга: {formatDateTime(lastLoadedAt)}</span>
            </div>
            <div className="db-panel-pad">
                {panelError ? <p className="db-error-text" style={{ marginBottom: 12 }}>{panelError}</p> : null}
                <div className="db-tbl-wrap">
                    <table className="db-tbl">
                        <thead>
                            <tr>
                                <th>Магазин</th>
                                <th className="num">Рейтинг</th>
                                <th className="num">Заказы</th>
                                <th className="num">Товары</th>
                                <th className="num">Отказы</th>
                                <th className="num">Просрочка</th>
                                <th className="num">Необработано</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const rating = Number(row.source_rating);
                                const ratingPct = Number.isFinite(rating) ? Math.max(0, Math.min(100, (rating / 5) * 100)) : 0;
                                return (
                                    <tr key={row.id}>
                                        <td>
                                            <div className="db-nm">{row.store_name || row.source_trade_brand || "—"}</div>
                                            <div className="db-sub-meta">{row.pharmacy_code}</div>
                                        </td>
                                        <td className="num">
                                            <span className={`db-rating ${ratingToneClass(row.source_rating)}`}>
                                                <span className="num">{formatAmount(row.source_rating)}</span>
                                                <span className="bar"><i style={{ width: `${ratingPct}%` }} /></span>
                                            </span>
                                        </td>
                                        <td className="num">{formatDays(row.total_orders).replace(",0", "")}</td>
                                        <td className="num">{formatDays(row.products_count).replace(",0", "")}</td>
                                        <td className="num"><RiskMetric label="Отказы" block={row.refusal} /></td>
                                        <td className="num"><RiskMetric label="Просрочка" block={row.shipping} /></td>
                                        <td className="num"><RiskMetric label="Необраб." block={row.unprocessed} /></td>
                                    </tr>
                                );
                            })}
                            {!rows.length ? (
                                <tr className="db-empty-row"><td colSpan={7}>{loading ? "Загрузка..." : "Нет данных рейтинга."}</td></tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
};

export default DashboardPage;
